import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { productService } from '../services/productService';
import { paymentService } from '../services/paymentService';
import { formatPrice } from '../utils/formatters';

const API_BASE = process.env.REACT_APP_API_URL || 'https://lixing-be.onrender.com';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [, setRetryCount] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // 添加选中图片索引状态

  const fetchProduct = useCallback(async (productId: string) => {
    try {
      setLoading(true);
      console.log('[产品详情] 开始获取产品:', productId);
      const data = await productService.getProduct(productId);
      console.log('[产品详情] 获取产品成功:', data.name);
      setProduct(data);
      setSelectedImageIndex(0); // 重置选中图片索引
      setRetryCount(0); // 重置重试计数
    } catch (err) {
      console.error('[产品详情] 获取产品失败:', err);
      setRetryCount((prev) => {
        const newRetryCount = prev + 1;
        if (newRetryCount >= 3) {
          setError('产品加载失败，请稍后重试');
        }
        return newRetryCount;
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      console.log('[产品详情] 组件挂载，产品ID:', id);
      fetchProduct(id);
      // 滚动到页面顶部
      window.scrollTo(0, 0);
    }
  }, [id, fetchProduct]);

  // 检查用户是否已购买该产品
  useEffect(() => {
    if (product?.id) {
      console.log('[产品详情] 检查购买状态，产品ID:', product.id);
      setHasPurchased(false); // 重置购买状态
      
      // 从localStorage获取用户信息
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const userData = JSON.parse(userStr);
          const userEmail = userData.email;
          
          // 调用API检查购买状态
          productService.checkPurchaseStatus(product.id, userEmail)
            .then((data: any) => {
              console.log('[产品详情] 购买状态检查结果:', data);
              if (data.has_purchased) {
                setHasPurchased(true);
              }
            })
            .catch((error: any) => {
              console.error('[产品详情] 购买状态检查失败:', error);
              // 如果API失败，尝试使用localStorage
              const lastOrder = localStorage.getItem('last_order');
              if (lastOrder) {
                try {
                  const orderData = JSON.parse(lastOrder);
                  if (orderData.productId === product.id) {
                    console.log('[产品详情] 从localStorage确认已购买');
                    setHasPurchased(true);
                  }
                } catch (e) {
                  console.error('[产品详情] 解析订单信息失败:', e);
                }
              }
            });
        } catch (e) {
          console.error('[产品详情] 解析用户数据失败:', e);
        }
      } else {
        // 用户未登录，检查localStorage中的最近订单
        const lastOrder = localStorage.getItem('last_order');
        if (lastOrder) {
          try {
            const orderData = JSON.parse(lastOrder);
            if (orderData.productId === product.id) {
              console.log('[产品详情] 从localStorage确认已购买（未登录用户）');
              setHasPurchased(true);
            }
          } catch (e) {
            console.error('[产品详情] 解析订单信息失败:', e);
          }
        }
      }
    }
  }, [product?.id]); // 只依赖product.id，避免无限循环

  const handlePurchase = async () => {
    if (!product) return;
    
    // 检查用户是否已登录
    const userStr = localStorage.getItem('user');
    if (!userStr) {
             // 未登录，跳转到登录页面
       navigate('/login', { state: { from: `/product/${product.id}` } });
      return;
    }
    
    // 如果已购买，直接跳转到下载页面
    if (hasPurchased) {
      navigate(`/download/${product.id}`);
      return;
    }
    
    // 如果是免费产品，直接跳转到下载页面（需要登录验证）
    if (product.price === 0) {
      navigate(`/download/${product.id}`);
      return;
    }
    
    // 已登录，进行支付
    setPurchasing(true);
    try {
      // 获取用户信息
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      // 使用支付宝支付接口（已正确配置success_url）
      const response = await paymentService.createAlipayPayment({
        product_id: product.id,
        amount: product.price,
        currency: 'cny',
        customer_email: user?.email,
        customer_name: user?.name
      });
      
      if (response.success && response.checkout_url) {
        // 直接重定向到支付宝支付页面
        window.location.href = response.checkout_url;
      } else {
        throw new Error('Failed to create Alipay payment');
      }
    } catch (err) {
      setError('支付初始化失败，请稍后重试');
      console.error('Payment error:', err);
    } finally {
      setPurchasing(false);
    }
  };

  // 渲染星级评分
  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}>
          ★
        </span>
      );
    }
    return stars;
  };

  // 处理产品图片URL
  const getImageUrl = (imgUrl: string): string => {
    if (!imgUrl) return '';
    
    // 如果已经是完整URL，直接返回
    if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://')) {
      return imgUrl;
    }
    
    // 如果是相对路径，添加API基础URL
    if (imgUrl.startsWith('/static') || imgUrl.startsWith('/api/files/')) {
      return `${API_BASE}${imgUrl}`;
    }
    
    // 如果是其他格式，尝试添加/static/images/前缀
    if (!imgUrl.startsWith('/')) {
      return `${API_BASE}/static/images/${imgUrl}`;
    }
    
    return `${API_BASE}${imgUrl}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || '产品不存在'}</p>
          <div className="space-x-4">
            <button
              onClick={() => {
                setError(null);
                setRetryCount(0);
                if (id) {
                  fetchProduct(id);
                }
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
            >
              重试
            </button>
            <button
              onClick={() => navigate('/products')}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
            >
              返回产品列表
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 处理产品图片URL
  const imgUrl = product.images && product.images.length > 0 && selectedImageIndex < product.images.length 
    ? product.images[selectedImageIndex] 
    : (product.images && product.images.length > 0 ? product.images[0] : '');
  const realUrl = getImageUrl(imgUrl);
  
  // 默认占位图片
  const defaultImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDMTE2LjU2OSA3MCAxMzAgODMuNDMxIDEzMCAxMDBDMTMwIDExNi41NjkgMTE2LjU2OSAxMzAgMTAwIDEzMEM4My40MzEgMTMwIDcwIDExNi41NjkgNzAgMTAwQzcwIDgzLjQzMSA4My40MzEgNzAgMTAwIDcwWiIgZmlsbD0iI0QxRDVEM0EiLz4KPHBhdGggZD0iTTEwMCAxNDBDMTE2LjU2OSAxNDAgMTMwIDE1My40MzEgMTMwIDE3MEMxMzAgMTg2LjU2OSAxMTYuNTY5IDIwMCAxMDAgMjAwQzgzLjQzMSAyMDAgNzAgMTg2LjU2OSA3MCAxNzBDNzAgMTUzLjQzMSA4My40MzEgMTQwIDEwMCAxNDBaIiBmaWxsPSIjRDFENUQzQSIvPgo8L3N2Zz4K';

  // 调试信息
  console.log('ProductDetail Debug:', {
    productName: product.name,
    images: product.images,
    selectedImageIndex,
    imgUrl,
    API_BASE,
    realUrl,
    finalUrl: realUrl || defaultImage
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 面包屑导航 */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex text-sm text-gray-600">
            <a href="/" className="hover:text-orange-500">首页</a>
            <span className="mx-2">/</span>
            <a href="/products" className="hover:text-orange-500">训练计划</a>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* 左侧：产品图片 */}
          <div className="space-y-4">
            <div className="relative">
              <img
                src={realUrl || defaultImage}
                alt={product.name}
                className="w-full h-96 object-cover rounded-lg shadow-lg"
                onError={(e) => {
                  console.error('图片加载失败:', realUrl);
                  const target = e.target as HTMLImageElement;
                  target.src = defaultImage;
                }}
                onLoad={() => {
                  console.log('图片加载成功:', realUrl);
                }}
              />
              {product.is_new && (
                <div className="absolute top-4 left-4">
                  <span className="bg-green-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                    新品
                  </span>
                </div>
              )}
            </div>
            
            {/* 缩略图 */}
            {product.images && product.images.length > 1 && (
              <div className="flex space-x-2">
                {product.images.map((image, index) => {
                  const imageUrl = getImageUrl(image);
                  return (
                    <img
                      key={index || 0}
                      src={imageUrl || defaultImage}
                      alt={`${product.name} ${(index || 0) + 1}`}
                      className={`w-20 h-20 object-cover rounded border-2 cursor-pointer hover:border-orange-500 transition-colors duration-200 ${
                        selectedImageIndex === index ? 'border-orange-500' : 'border-gray-200'
                      }`}
                      onClick={() => setSelectedImageIndex(index)}
                      onError={(e) => {
                        console.error('缩略图加载失败:', imageUrl);
                        const target = e.target as HTMLImageElement;
                        target.src = defaultImage;
                      }}
                      onLoad={() => {
                        console.log('缩略图加载成功:', imageUrl);
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* 右侧：产品信息 */}
          <div className="space-y-6">
            {/* 产品标题 */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  {renderStars(product.rating || 0)}
                  <span className="ml-2 text-sm text-gray-600">
                    {(product.rating || 0).toFixed(1)} ({product.reviews_count || 0} 评价)
                  </span>
                </div>
                <span className="text-sm text-gray-500">|</span>

              </div>
            </div>

            {/* 价格 */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {product.price === 0 ? '免费' : formatPrice(product.price, '¥')}
              </div>
              <p className="text-sm text-gray-600">数字产品 - 立即下载</p>
            </div>




            {/* 产品描述 */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">产品描述</h3>
              <p className="text-gray-600 leading-relaxed">{product.description}</p>
            </div>

                                    {/* 购买按钮 */}
            <div className="space-y-4">
              <button
                onClick={handlePurchase}
                disabled={purchasing}
                className={`w-full font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl ${
                  product.price === 0 || hasPurchased
                    ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white' 
                    : 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white'
                }`}
              >
                {purchasing ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    {product.price === 0 || hasPurchased ? '正在跳转下载...' : '正在跳转支付...'}
                  </div>
                ) : (
                  (() => {
                    const userStr = localStorage.getItem('user');
                    const isLoggedIn = !!userStr;
                    
                    if (product.price === 0) {
                      return isLoggedIn ? '免费下载' : '登录后免费下载';
                    } else if (hasPurchased) {
                      return '立即下载';
                    } else {
                      return isLoggedIn ? '立即购买' : '登录后购买';
                    }
                  })()
                )}
              </button>
              
              {/* 安全提示 */}
              {product.price > 0 && !hasPurchased && (
                <div className="text-center text-sm text-gray-500">
                  <p>🔒 安全支付 | 支持支付宝支付</p>
                </div>
              )}
              
              {/* 免费提示 */}
              {product.price === 0 && (
                <div className="text-center text-sm text-green-600">
                  <p>🎉 免费产品 | 无需支付，直接下载</p>
                </div>
              )}
              
              {/* 已购买提示 */}
              {product.price > 0 && hasPurchased && (
                <div className="text-center text-sm text-green-600">
                  <p>✅ 已购买 | 可直接下载</p>
                </div>
              )}
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default ProductDetail; 
